import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Reveal, staggerContainer, staggerItem } from "@/components/motion";
import { useTranslation } from "react-i18next";
import {
  MessageCircle,
  Shield,
  Heart,
  Target,
  Sparkles,
  CloudRain,
  Wrench,
  ShieldAlert,
  ArrowRight,
  Check,
  Lightbulb,
  RefreshCw,
  Lock,
  Ban,
} from "lucide-react";
import { LandingPageLayout } from "../shared/layout";

const scenarioIcons = [Heart, MessageCircle, Shield, Sparkles, Wrench, CloudRain];
const stepIcons = [MessageCircle, Sparkles, Target];
const trustIcons = [Shield, Ban, Lock];

export function LandingPageScreen() {
  const { t } = useTranslation();
  const scenarios = t("landing.home.scenarios.items", { returnObjects: true });
  const steps = t("landing.home.howItWorks.steps", { returnObjects: true });
  const heroBenefits = t("landing.home.hero.benefits", { returnObjects: true });
  const redFlagFeatures = t("landing.home.redFlag.features", { returnObjects: true });
  const trustBadges = t("landing.home.trust.badges", { returnObjects: true });
  const trustCards = t("landing.home.trust.cards", { returnObjects: true });

  return (
    <LandingPageLayout>
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,hsl(var(--accent)/0.75),hsl(var(--background))_55%,hsl(var(--secondary)/0.55))]" />
        <div className="container mx-auto px-4 py-10 md:py-12">
          <div className="grid lg:grid-cols-[1fr_0.92fr] gap-8 lg:gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <Badge variant="secondary" className="mb-5 px-4 py-1 rounded-full border border-primary/15">
                  {t("landing.home.hero.badge")}
                </Badge>
                <h1 className="text-[2.45rem] md:text-[3.6rem] font-semibold leading-[1.03] mb-5 max-w-3xl">
                  {t("landing.home.hero.title")}
                  <span className="text-primary"> {t("landing.home.hero.titleHighlight")}</span>
                </h1>
                <p className="text-lg text-muted-foreground mb-8 max-w-xl leading-relaxed">
                  {t("landing.home.hero.description")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <Button size="lg" className="h-12 px-7 text-base" asChild>
                    <Link to="/register">
                      {t("landing.home.hero.primaryCta")} <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="h-12 px-7 text-base" asChild>
                    <Link to="/red-flag-check">{t("landing.home.hero.secondaryCta")}</Link>
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  {heroBenefits.map((benefit) => (
                    <span key={benefit} className="inline-flex items-center gap-1.5">
                      <Check className="h-4 w-4 text-primary" /> {benefit}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div
              className="relative mx-auto w-full max-w-[20rem] md:max-w-[22rem] max-h-[270px] lg:max-h-none overflow-hidden lg:overflow-visible"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="rounded-[2rem] border-[8px] border-foreground/85 bg-foreground shadow-2xl overflow-hidden">
                <div className="bg-card rounded-[1.45rem] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b bg-secondary/70">
                    <div>
                      <p className="text-[11px] uppercase font-semibold text-primary">{t("landing.home.productMockup.label")}</p>
                      <p className="text-xs text-muted-foreground">{t("landing.home.productMockup.sessionTitle")}</p>
                    </div>
                    <span className="text-[11px] rounded-full bg-primary/10 text-primary px-2 py-1">
                      {t("landing.home.productMockup.live")}
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-end gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><Sparkles className="h-3.5 w-3.5" /></div>
                      <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-3.5 py-2 text-sm max-w-[78%]">
                        {t("landing.home.productMockup.messages.ai1")}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-3.5 py-2 text-sm max-w-[80%]">
                        {t("landing.home.productMockup.messages.user")}
                      </div>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><Sparkles className="h-3.5 w-3.5" /></div>
                      <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-3.5 py-2 text-sm max-w-[78%]">
                        {t("landing.home.productMockup.messages.ai2")}
                      </div>
                    </div>
                  </div>
                  <div className="mx-4 mb-3 rounded-xl border bg-accent/45 p-3">
                    <p className="text-xs font-medium text-primary mb-2 flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5" /> {t("landing.home.productMockup.feedbackTitle")}
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug mb-3">
                      {t("landing.home.productMockup.feedback")}
                    </p>
                    <div className="space-y-2 text-[11px] text-muted-foreground">
                      {[
                        [t("landing.home.productMockup.scores.clarity"), 80],
                        [t("landing.home.productMockup.scores.empathy"), 90],
                        [t("landing.home.productMockup.scores.boundaries"), 100],
                      ].map(([label, width]) => (
                        <div key={label}>
                          <div className="flex justify-between mb-1"><span>{label}</span><b className="text-primary">{width / 10}</b></div>
                          <div className="h-1.5 rounded-full bg-background overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${width}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 p-4 pt-0">
                    <Button size="sm" variant="outline" className="h-9 text-xs">
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> {t("landing.home.productMockup.retry")}
                    </Button>
                    <Button size="sm" className="h-9 text-xs">
                      {t("landing.home.productMockup.next")}
                    </Button>
                  </div>
                </div>
              </div>

              <motion.div
                className="absolute -right-5 -bottom-5 bg-card border shadow-lg p-3 w-48 hidden sm:block rounded-xl"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.4 }}
              >
                <p className="text-[11px] font-medium inline-flex items-center gap-1.5 text-destructive mb-0.5">
                  <ShieldAlert className="h-3 w-3" /> {t("landing.home.productMockup.redFlagTitle")}
                </p>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {t("landing.home.productMockup.redFlagText")}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold mb-3">{t("landing.home.howItWorks.title")}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t("landing.home.howItWorks.description")}
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((s, i) => {
              const StepIcon = stepIcons[i];
              return (
                <Reveal key={s.title} delay={i * 0.1}>
                  <div className="relative border bg-card p-6 h-full rounded-lg">
                    <div className="absolute -top-3 left-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1">
                      0{i + 1}
                    </div>
                    <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-5">
                      <StepIcon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-medium text-lg mb-2">{s.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/45 border-y">
        <div className="container mx-auto px-4">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold mb-3">{t("landing.home.scenarios.title")}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t("landing.home.scenarios.description")}
            </p>
          </Reveal>
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
          >
            {scenarios.map(({ title, text }, index) => {
              const Icon = scenarioIcons[index];
              return (
              <motion.div key={title} variants={staggerItem}>
                <Card className="h-full transition-shadow hover:shadow-lg rounded-lg">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-muted-foreground">{text}</CardContent>
                </Card>
              </motion.div>
              );
            })}
          </motion.div>
          <Reveal delay={0.1} className="text-center mt-10">
            <Button variant="ghost" className="text-primary" asChild>
              <Link to="/register">{t("landing.home.scenarios.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Badge variant="secondary" className="mb-4 rounded-full">{t("landing.home.redFlag.badge")}</Badge>
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">{t("landing.home.redFlag.title")}</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {t("landing.home.redFlag.description")}
              </p>
              <ul className="space-y-3 mb-8">
                {redFlagFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <Button asChild><Link to="/red-flag-check">{t("landing.home.redFlag.cta")} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            </Reveal>

            <Reveal delay={0.1}>
              <Card className="shadow-xl rounded-lg border-destructive/20">
                <CardContent className="p-5 space-y-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium bg-destructive/10 text-destructive">
                    <ShieldAlert className="h-4 w-4" /> {t("landing.home.redFlag.card.status")}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t("landing.home.redFlag.card.summary")}
                  </p>
                  <div className="space-y-2">
                    <div className="rounded-lg border p-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{t("landing.home.redFlag.card.pattern1")}</span>
                        <Badge className="bg-destructive/10 text-destructive" variant="secondary">{t("landing.home.redFlag.card.high")}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{t("landing.home.redFlag.card.quote1")}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{t("landing.home.redFlag.card.pattern2")}</span>
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100" variant="secondary">{t("landing.home.redFlag.card.medium")}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{t("landing.home.redFlag.card.quote2")}</p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-muted p-3">
                    <p className="text-xs font-medium flex items-center gap-1.5 mb-1"><Lightbulb className="h-3.5 w-3.5" /> {t("landing.home.redFlag.card.replyTitle")}</p>
                    <p className="text-sm italic">{t("landing.home.redFlag.card.reply")}</p>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/45 border-y">
        <div className="container mx-auto px-4 max-w-3xl">
          <Reveal>
            <div className="border bg-card p-8 md:p-12 rounded-lg">
              <p className="text-sm font-medium text-primary mb-4">{t("landing.home.founderNote.eyebrow")}</p>
              <p className="text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: '"Fraunces", serif' }}>
                {t("landing.home.founderNote.quote")}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t("landing.home.founderNote.body1")}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t("landing.home.founderNote.body2")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <Reveal className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-semibold mb-5">{t("landing.home.trust.title")}</h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              {t("landing.home.trust.description")}
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto mb-8">
            {trustCards.map((card, index) => {
              const Icon = trustIcons[index];
              return (
                <Reveal key={card.title} delay={index * 0.08}>
                  <div className="h-full border bg-card p-6 rounded-lg">
                    <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-medium mb-2">{card.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{card.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="flex flex-wrap justify-center gap-3 text-sm">
              {trustBadges.map((b) => (
                <Badge key={b} variant="outline" className="px-3 py-1">{b}</Badge>
              ))}
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-semibold mb-5">{t("landing.home.cta.title")}</h2>
            <p className="text-lg opacity-90 mb-9 max-w-xl mx-auto">{t("landing.home.cta.description")}</p>
            <Button size="lg" variant="secondary" className="h-12 px-8 text-base text-primary" asChild>
              <Link to="/register">{t("landing.home.cta.button")}</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </LandingPageLayout>
  );
}
